import { useCallback, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import {
  activateCatalogEntity,
  approveVersion,
  CATALOG_FIELD_LABELS,
  getUsages,
  getValidation,
  getVersions,
  type CatalogUsageRow,
  type CatalogValidation,
  type CatalogVersion,
} from '../api/catalogGovernance'
import { Button, Callout, Card, CardContent, CardHeader, DataTable, EmptyState, LoadingState, Page, PageHeader, StatusPill } from '../components/ui'

/**
 * F-067 (TASK-0321, REQ-293-001, REQ-294-001, REQ-257-001): governance di un prodotto o di un operatore.
 * Tre blocchi: verifica di configurazione (cosa impedisce l'attivazione), dove e' usato, storico versioni
 * con le differenze. Rotta `/products/:id/governance`; per un operatore `?tipo=operatore`. Tutto il
 * server e' riservato agli Admin: per gli altri la pagina mostra l'errore 403 del server.
 */
function formatValue(v: string | null): string {
  if (v === null) return '(vuoto)'
  if (v === 'true') return 'Si'
  if (v === 'false') return 'No'
  return v
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', { timeZone: 'Europe/Rome' })
}

export function CatalogGovernancePage() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const entityId = Number(id)
  const isOperator = params.get('tipo') === 'operatore'
  const kind = isOperator ? 'operators' : 'products'
  const label = isOperator ? 'operatore' : 'prodotto'

  const [name, setName] = useState('')
  const [isActive, setIsActive] = useState<boolean | null>(null)
  const [validation, setValidation] = useState<CatalogValidation | null>(null)
  const [usages, setUsages] = useState<CatalogUsageRow[]>([])
  const [versions, setVersions] = useState<CatalogVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activating, setActivating] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [approvingId, setApprovingId] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [v, u, ver] = await Promise.all([
        getValidation(kind, entityId),
        getUsages(kind, entityId),
        getVersions(kind, entityId),
      ])
      setValidation(v)
      setUsages(u)
      setVersions(ver)
      const head = isOperator
        ? await api<{ name: string; isActive: boolean }>(`/api/operators/${entityId}`)
        : (await api<{ product: { name: string; isActive: boolean } }>(`/api/products/${entityId}`)).product
      setName(head.name)
      setIsActive(head.isActive)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Errore di caricamento')
    } finally {
      setLoading(false)
    }
  }, [kind, entityId, isOperator])

  useEffect(() => {
    if (!Number.isFinite(entityId)) return
    void load()
  }, [load, entityId])

  /** F-142 (REQ-220-002): firma del secondo controllore; il server rifiuta (409) l'autore e le versioni gia' firmate. */
  async function handleApprove(versionId: number) {
    setApprovingId(versionId)
    setMessage(null)
    try {
      await approveVersion(versionId)
      setMessage('Versione approvata.')
      await load()
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : 'Errore durante l\'approvazione')
    } finally {
      setApprovingId(null)
    }
  }

  async function handleActivate() {
    setActivating(true)
    setMessage(null)
    try {
      const res = await activateCatalogEntity(kind, entityId)
      setMessage(res.ok ? `L'${label} e' attivo.` : `Attivazione rifiutata: ${res.errors.map(e => e.message).join(' ')}`)
      await load()
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : 'Errore durante l\'attivazione')
    } finally {
      setActivating(false)
    }
  }

  const shownUsages = usages.filter(u => u.count > 0)

  return (
    <Page>
      <PageHeader
        title={
          <>
            Governance {label}: {name || `#${id}`}{' '}
            {isActive === null ? null : isActive
              ? <StatusPill tone="success">Attivo</StatusPill>
              : <StatusPill tone="neutral">Inattivo</StatusPill>}
          </>
        }
        icon="inventory_2"
        back={{ to: '/products', label: 'Prodotti' }}
      />

      {loading && <LoadingState />}
      {error && <Callout tone="error">{error}</Callout>}
      {message && <Callout tone="info">{message}</Callout>}

      {!loading && !error && (
        <>
          <Card variant="outlined">
            <CardHeader
              title="Verifica di configurazione"
              actions={isActive === false && (
                <Button type="button" variant="tonal" size="sm" disabled={activating} onClick={() => void handleActivate()}>
                  Attiva
                </Button>
              )}
            />
            <CardContent>
              <div className="md-stack">
                {validation?.isValid && validation.warnings.length === 0 && (
                  <Callout tone="success">La configurazione e' completa: nessun problema.</Callout>
                )}
                {validation?.errors.map(e => (
                  <Callout key={`${e.code}-${e.message}`} tone="error">Errore: {e.message}</Callout>
                ))}
                {validation?.warnings.map(w => (
                  <Callout key={`${w.code}-${w.message}`} tone="warning">Avviso: {w.message}</Callout>
                ))}
                {validation && !validation.isValid && (
                  <div className="md-typescale-body-small">Finche' ci sono errori l'{label} non si puo' attivare.</div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card variant="outlined" padding="none">
            <CardHeader title="Dove e' usato" />
            {shownUsages.length === 0 ? (
              <EmptyState compact icon="link_off" title="Nessun uso registrato." />
            ) : (
              <DataTable variant="plain" aria-label="Utilizzi">
                <tbody>
                  {shownUsages.map(u => (
                    <tr key={u.area}>
                      <td>{u.label}</td>
                      <td className="md-num">{u.count}</td>
                      <td className="md-num">
                        {u.blocking ? <StatusPill tone="warning">Richiede conferma</StatusPill> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            )}
          </Card>

          <Card variant="outlined">
            <CardHeader title="Storico versioni" />
            <CardContent>
              {versions.length === 0 && <EmptyState compact icon="history" title="Nessuna modifica registrata." />}
              {versions.map(v => (
                <div key={v.version} className="border-bottom pb-2 mb-2">
                  <div>
                    <strong>Versione {v.version}</strong> &middot; valida dal {v.validFrom} &middot; {formatDateTime(v.createdAt)}
                    {v.createdByUserId !== null && <> &middot; utente {v.createdByUserId}</>}
                  </div>
                  <div className="md-typescale-body-small">Motivazione: {v.reason}</div>
                  {/* F-142 (REQ-220-002): la baseline (versione 1 senza modifiche) non si firma. */}
                  <div className="md-cluster md-typescale-body-small mt-1">
                    <span>Approvata da:</span>
                    {v.approvedBy !== null ? (
                      <StatusPill tone="success">
                        utente {v.approvedBy}{v.approvedAt ? ` il ${formatDateTime(v.approvedAt)}` : ''}
                      </StatusPill>
                    ) : v.changes.length > 0 ? (
                      <>
                        <span>in attesa</span>
                        <Button
                          type="button"
                          variant="outlined"
                          size="sm"
                          disabled={approvingId === v.id}
                          onClick={() => void handleApprove(v.id)}
                        >
                          Approva
                        </Button>
                      </>
                    ) : (
                      <span>non richiesta</span>
                    )}
                    {v.approvalNote && <span>Nota: {v.approvalNote}</span>}
                  </div>
                  {v.changes.length > 0 && (
                    <DataTable variant="plain" aria-label={`Modifiche della versione ${v.version}`}>
                      <thead>
                        <tr><th>Campo</th><th>Prima</th><th>Dopo</th></tr>
                      </thead>
                      <tbody>
                        {v.changes.map(c => (
                          <tr key={c.field}>
                            <td>{CATALOG_FIELD_LABELS[c.field] ?? c.field}</td>
                            <td>{formatValue(c.before)}</td>
                            <td>{formatValue(c.after)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </DataTable>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </Page>
  )
}
